import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';

import { API_BASE_URL } from '../../config';
import axiosInstance from '../../services/axiosInstance';
import {
  getKnowledgeHubDocumentCoverage,
  getKnowledgeHubIndexCoverage,
  getKnowledgeHubAutoIndexStatus,
  answerKnowledgeHubQuestion,
  getKnowledgeHubStatus,
  lookupKnowledgeHubArticle,
  runKnowledgeHubDocumentIndex,
  runKnowledgeHubProfileIndex,
  runKnowledgeHubAssetIndexBatch,
  runKnowledgeHubVectorIndex,
  cancelKnowledgeHubAutoIndexJob,
  listKnowledgeHubAutoIndexJobs,
  retryKnowledgeHubAutoIndexJob,
  runKnowledgeHubAutoIndexOnce,
  searchKnowledgeHubEntities,
  searchKnowledgeHubHybrid,
  searchKnowledgeHubSemantic,
} from './api';
import {
  KnowledgeHubEntity,
  KnowledgeHubAnswerActiveContext,
  KnowledgeHubAnswerActiveResultSet,
  KnowledgeHubResponseConfidence,
  KnowledgeHubAnswerHistoryItem,
  KnowledgeHubAnswerResponse,
  KnowledgeHubDocumentCoverage,
  KnowledgeHubHybridSearchResponse,
  KnowledgeHubIndexCoverage,
  KnowledgeHubIndexSummary,
  KnowledgeHubSearchResponse,
  KnowledgeHubSemanticSearchResponse,
  KnowledgeHubStatus,
  KnowledgeHubAutoIndexJob,
  KnowledgeHubAutoIndexStatus,
  KnowledgeHubVectorIndexSummary,
} from './types';
import DocumentTextInspector from './DocumentTextInspector';
import { useKnowledgeHubAdminAccess } from './KnowledgeHubPermissionContext';

function getErrorMessage(error: unknown): string {
  const anyError = error as any;
  return anyError?.response?.data?.message || anyError?.response?.data?.error || anyError?.message || String(error);
}

const StatChip: React.FC<{ label: string; value: number; color?: 'default' | 'success' | 'warning' | 'error' | 'info' }> = ({
  label,
  value,
  color = 'default',
}) => (
  <Chip size="small" color={color} variant={color === 'default' ? 'outlined' : 'filled'} label={`${label}: ${value}`} />
);

type HybridEntityTypeFilter = 'all' | 'image' | 'recipe' | 'wine' | 'product' | 'document' | 'salesSheet' | 'asset';
type HybridFeedbackValue = 'good' | 'bad';
type KnowledgeHubChatSide = 'left' | 'right';

const CHAT_SIDE_STORAGE_KEY = 'knowledgeHubChatSide';
const CHAT_SIDE_EVENT = 'knowledgeHubChatSideChanged';

function readChatSide(): KnowledgeHubChatSide {
  try {
    return window.localStorage.getItem(CHAT_SIDE_STORAGE_KEY) === 'left' ? 'left' : 'right';
  } catch {
    return 'right';
  }
}

function writeChatSide(side: KnowledgeHubChatSide): void {
  try {
    window.localStorage.setItem(CHAT_SIDE_STORAGE_KEY, side);
    window.dispatchEvent(new CustomEvent(CHAT_SIDE_EVENT, { detail: { side } }));
  } catch {
    // localStorage can be unavailable in restrictive browser modes.
  }
}

function getConfidenceLevel(confidence: KnowledgeHubResponseConfidence): string {
  return typeof confidence === 'string' ? confidence : confidence.level;
}

function getConfidenceLabel(confidence: KnowledgeHubResponseConfidence): string {
  const level = getConfidenceLevel(confidence);
  if (level === 'high') return 'Hög säkerhet';
  if (level === 'low') return 'Låg säkerhet';
  return 'Medel säkerhet';
}

function getConfidenceColor(confidence: KnowledgeHubResponseConfidence): 'success' | 'warning' | 'default' {
  const level = getConfidenceLevel(confidence);
  if (level === 'high') return 'success';
  if (level === 'low') return 'warning';
  return 'default';
}

function getAnswerParagraphs(answer: string): string[] {
  return String(answer || '')
    .replace(/\*\*/g, '')
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function buildAnswerActiveContext(entity: KnowledgeHubEntity): KnowledgeHubAnswerActiveContext {
  return {
    entityId: entity._id,
    assetId: getEntityAssetId(entity) || undefined,
    title: entity.title,
    entityType: entity.entityType,
    fileName: entity.metadata?.fileName,
    articleNumbers: entity.articleNumbers || [],
  };
}

function formatAnswerHistory(
  history: KnowledgeHubAnswerHistoryItem[],
  activeContext: KnowledgeHubAnswerActiveContext | null,
  activeResultSet: KnowledgeHubAnswerActiveResultSet | null,
): string {
  const contextLine = activeContext
    ? `Aktiv context: ${activeContext.title}${activeContext.fileName ? ` (${activeContext.fileName})` : ''}`
    : 'Aktiv context: ingen';
  const resultSetLine = activeResultSet?.items.length
    ? `Aktiv lista: ${activeResultSet.items.length} träffar (${activeResultSet.items.slice(0, 8).map((item) => item.title).join(', ')})`
    : 'Aktiv lista: ingen';

  return [
    contextLine,
    resultSetLine,
    '',
    ...history.map((item) => `${item.role === 'user' ? 'Användare' : 'Assistent'}: ${item.content}`),
  ].join('\n');
}

function buildAnswerActiveResultSet(results: KnowledgeHubHybridSearchResponse['results']): KnowledgeHubAnswerActiveResultSet {
  return {
    items: results.slice(0, 100).map((result) => ({
      entityId: result.entity._id,
      title: result.entity.title,
      entityType: result.entity.entityType,
      fileName: result.entity.metadata?.fileName,
      articleNumbers: result.entity.articleNumbers || [],
    })),
  };
}

function formatBytes(value?: number): string {
  if (!value || !Number.isFinite(value)) return '';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function absoluteBackendUrl(path?: string): string {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

function getEntityAssetId(entity: KnowledgeHubEntity): string {
  return String(entity.source?.assetId || (entity.source?.collection === 'assets' ? entity.source?.id : '') || '');
}

function openAssetDetails(entity: KnowledgeHubEntity): void {
  const assetId = getEntityAssetId(entity);
  if (!assetId) return;

  window.dispatchEvent(new CustomEvent('dam:open-asset-details', {
    detail: { assetId, preserveCurrentView: true },
  }));
}

async function downloadEntityAsset(entity: KnowledgeHubEntity): Promise<void> {
  const assetId = getEntityAssetId(entity);
  if (!assetId) return;

  const fileName = entity.metadata?.fileName || entity.title || 'asset';
  const response = await axiosInstance.get(`/api/assets/${assetId}/download`, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function normalizeFilterText(value: unknown): string {
  return String(value || '').trim().toLocaleLowerCase('sv-SE');
}

function entityFilterHaystack(entity: KnowledgeHubEntity): string {
  const metadata = entity.metadata || {};
  return [
    entity.entityType,
    entity.title,
    metadata.fileName,
    metadata.templateName,
    ...(entity.tags || []),
    ...(metadata.categoryNames || []),
  ].map(normalizeFilterText).filter(Boolean).join(' ');
}

function isBannerEntity(entity: KnowledgeHubEntity): boolean {
  return entityFilterHaystack(entity).includes('banner');
}

function isRecipeEntity(entity: KnowledgeHubEntity): boolean {
  return entity.entityType === 'recipe';
}

const EntityCard: React.FC<{ entity: KnowledgeHubEntity }> = ({ entity }) => {
  const metadata = entity.metadata || {};
  const categories = metadata.categoryNames || [];
  const previewPath = metadata.previewPaths?.thumbnail || metadata.previewPaths?.medium;
  const previewUrl = absoluteBackendUrl(previewPath);
  const assetId = getEntityAssetId(entity);
  const dimensions = metadata.width && metadata.height ? `${metadata.width}x${metadata.height}` : '';
  const fileSize = formatBytes(metadata.fileSize);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {previewUrl && (
            <Box
              component="img"
              src={previewUrl}
              alt={entity.title}
              loading="lazy"
              sx={{
                width: { xs: '100%', sm: 148 },
                height: 112,
                objectFit: 'contain',
                bgcolor: '#f7f8fa',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
              }}
            />
          )}
          <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip size="small" color="primary" label={entity.entityType} />
              {entity.articleNumbers?.slice(0, 4).map((articleNumber) => (
                <Chip key={articleNumber} size="small" variant="outlined" label={articleNumber} />
              ))}
            </Stack>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {entity.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {metadata.fileName || entity.source?.path || entity.source?.id}
            </Typography>
            {(metadata.templateName || categories.length > 0) && (
              <Typography variant="caption" color="text.secondary">
                {[metadata.templateName, ...categories].filter(Boolean).join(' | ')}
              </Typography>
            )}
            {(metadata.fileType || dimensions || fileSize) && (
              <Typography variant="caption" color="text.secondary">
                {[metadata.fileType, dimensions, fileSize].filter(Boolean).join(' | ')}
              </Typography>
            )}
            {entity.tags && entity.tags.length > 0 && (
              <Stack direction="row" spacing={0.75} flexWrap="wrap">
                {entity.tags.slice(0, 10).map((tag) => (
                  <Chip key={tag} size="small" variant="outlined" label={tag} />
                ))}
              </Stack>
            )}
            {assetId && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => openAssetDetails(entity)}>
                  Öppna asset
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    void downloadEntityAsset(entity);
                  }}
                >
                  Ladda ner
                </Button>
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const KnowledgeHubTestPageContent: React.FC = () => {
  const [status, setStatus] = useState<KnowledgeHubStatus | null>(null);
  const [chatSide, setChatSide] = useState<KnowledgeHubChatSide>(() => readChatSide());
  const [indexLimit, setIndexLimit] = useState(10);
  const [vectorLimit, setVectorLimit] = useState(10);
  const [documentLimit, setDocumentLimit] = useState(25);
  const [indexing, setIndexing] = useState(false);
  const [fullIndexing, setFullIndexing] = useState(false);
  const [documentIndexing, setDocumentIndexing] = useState(false);
  const [documentForceIndexing, setDocumentForceIndexing] = useState(false);
  const [profileIndexing, setProfileIndexing] = useState(false);
  const [vectorIndexing, setVectorIndexing] = useState(false);
  const [fullVectorIndexing, setFullVectorIndexing] = useState(false);
  const [indexSummary, setIndexSummary] = useState<KnowledgeHubIndexSummary | null>(null);
  const [indexCoverage, setIndexCoverage] = useState<KnowledgeHubIndexCoverage | null>(null);
  const [documentCoverage, setDocumentCoverage] = useState<KnowledgeHubDocumentCoverage | null>(null);
  const [autoIndexStatus, setAutoIndexStatus] = useState<KnowledgeHubAutoIndexStatus | null>(null);
  const [autoIndexJobs, setAutoIndexJobs] = useState<KnowledgeHubAutoIndexJob[]>([]);
  const [vectorSummary, setVectorSummary] = useState<KnowledgeHubVectorIndexSummary | null>(null);
  const [documentSummary, setDocumentSummary] = useState<KnowledgeHubIndexSummary | null>(null);
  const [indexOffset, setIndexOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState('Saganaki');
  const [semanticQuery, setSemanticQuery] = useState('sommarbild med lite blått');
  const [hybridQuery, setHybridQuery] = useState('17548');
  const [hybridTypeFilter, setHybridTypeFilter] = useState<HybridEntityTypeFilter>('all');
  const [hybridOnlyImages, setHybridOnlyImages] = useState(false);
  const [hybridExcludeBanner, setHybridExcludeBanner] = useState(false);
  const [hybridExcludeRecipe, setHybridExcludeRecipe] = useState(false);
  const [hybridFeedback, setHybridFeedback] = useState<Record<string, HybridFeedbackValue>>({});
  const [articleNumber, setArticleNumber] = useState('V198');
  const [searchResults, setSearchResults] = useState<KnowledgeHubSearchResponse | null>(null);
  const [semanticResults, setSemanticResults] = useState<KnowledgeHubSemanticSearchResponse | null>(null);
  const [hybridResults, setHybridResults] = useState<KnowledgeHubHybridSearchResponse | null>(null);
  const [knowledgeAnswer, setKnowledgeAnswer] = useState<KnowledgeHubAnswerResponse | null>(null);
  const [answerHistory, setAnswerHistory] = useState<KnowledgeHubAnswerHistoryItem[]>([]);
  const [activeAnswerContext, setActiveAnswerContext] = useState<KnowledgeHubAnswerActiveContext | null>(null);
  const [activeAnswerResultSet, setActiveAnswerResultSet] = useState<KnowledgeHubAnswerActiveResultSet | null>(null);
  const [articleResults, setArticleResults] = useState<KnowledgeHubSearchResponse | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingCoverage, setLoadingCoverage] = useState(false);
  const [loadingAutoIndex, setLoadingAutoIndex] = useState(false);
  const [autoIndexAction, setAutoIndexAction] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [semanticSearching, setSemanticSearching] = useState(false);
  const [hybridSearching, setHybridSearching] = useState(false);
  const [answering, setAnswering] = useState(false);
  const [lookingUpArticle, setLookingUpArticle] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      setStatus(await getKnowledgeHubStatus());
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const loadIndexCoverage = useCallback(async () => {
    try {
      setLoadingCoverage(true);
      const [assetCoverage, nextDocumentCoverage] = await Promise.all([
        getKnowledgeHubIndexCoverage(),
        getKnowledgeHubDocumentCoverage(),
      ]);
      setIndexCoverage(assetCoverage);
      setDocumentCoverage(nextDocumentCoverage);
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setLoadingCoverage(false);
    }
  }, []);

  const loadAutoIndexQueue = useCallback(async () => {
    try {
      setLoadingAutoIndex(true);
      const [queueStatus, jobs] = await Promise.all([
        getKnowledgeHubAutoIndexStatus(),
        listKnowledgeHubAutoIndexJobs({ limit: 10 }),
      ]);
      setAutoIndexStatus(queueStatus);
      setAutoIndexJobs(jobs);
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setLoadingAutoIndex(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
    void loadIndexCoverage();
    void loadAutoIndexQueue();
  }, [loadStatus, loadIndexCoverage, loadAutoIndexQueue]);

  const handleRunIndex = async () => {
    try {
      setIndexing(true);
      setMessage({ type: 'info', text: `Indexerar upp till ${indexLimit} assets...` });
      const summary = await runKnowledgeHubAssetIndexBatch(indexLimit, 0);
      setIndexSummary(summary);
      setIndexCoverage(summary.coverage || await getKnowledgeHubIndexCoverage());
      setIndexOffset(indexLimit);
      setMessage({
        type: summary.status === 'completed' ? 'success' : 'error',
        text: summary.status === 'completed' ? 'Indexering klar.' : 'Indexering klar med fel. Se summeringen.',
      });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setIndexing(false);
    }
  };

  const handleRunNextIndexBatch = async () => {
    try {
      setIndexing(true);
      setMessage({ type: 'info', text: `Indexerar batch från offset ${indexOffset}...` });
      const summary = await runKnowledgeHubAssetIndexBatch(indexLimit, indexOffset);
      setIndexSummary(summary);
      setIndexCoverage(summary.coverage || await getKnowledgeHubIndexCoverage());
      setIndexOffset(indexOffset + indexLimit);
      setMessage({
        type: summary.status === 'completed' ? 'success' : 'error',
        text: summary.status === 'completed' ? 'Batch-indexering klar.' : 'Batch-indexering klar med fel.',
      });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setIndexing(false);
    }
  };

  const handleRunFullAssetIndex = async () => {
    try {
      setFullIndexing(true);
      const initialCoverage = indexCoverage || await getKnowledgeHubIndexCoverage();
      const totalAssets = initialCoverage.assets.total;
      const batchSize = indexLimit;
      let latestSummary: KnowledgeHubIndexSummary | null = null;

      setMessage({ type: 'info', text: `Startar full batch-indexering av ${totalAssets} assets...` });
      for (let offset = 0; offset < totalAssets; offset += batchSize) {
        setIndexOffset(offset);
        setMessage({ type: 'info', text: `Indexerar assets ${offset + 1}-${Math.min(offset + batchSize, totalAssets)} av ${totalAssets}...` });
        latestSummary = await runKnowledgeHubAssetIndexBatch(batchSize, offset);
        setIndexSummary(latestSummary);
        if (latestSummary.coverage) {
          setIndexCoverage(latestSummary.coverage);
        }
        if (latestSummary.status !== 'completed') break;
      }

      const coverage = await getKnowledgeHubIndexCoverage();
      setIndexCoverage(coverage);
      setIndexOffset(totalAssets);
      setMessage({
        type: latestSummary?.status === 'failed' ? 'error' : 'success',
        text: latestSummary?.status === 'failed'
          ? 'Full indexering avbröts med fel. Se summeringen.'
          : `Full indexering klar. Täckning: ${coverage.assets.indexed} av ${coverage.assets.total} assets.`,
      });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setFullIndexing(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      setSearching(true);
      setSearchResults(await searchKnowledgeHubEntities(searchQuery.trim(), 12));
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setSearching(false);
    }
  };

  const handleRunDocumentIndex = async () => {
    try {
      setDocumentIndexing(true);
      setMessage({ type: 'info', text: `Dokumentindexerar alla källor i batchar om ${documentLimit}...` });
      const coverageBefore = await getKnowledgeHubDocumentCoverage();
      const expectedTotal = Object.values(coverageBefore.bySourceKind || {})
        .reduce((sum, item) => sum + Number(item.total || 0), 0);
      const maxBatches = Math.max(2, Math.ceil(Math.max(expectedTotal, documentLimit) / documentLimit) + 2);
      let offset = 0;
      let lastSummary: KnowledgeHubIndexSummary | null = null;
      const aggregate = { scanned: 0, created: 0, updated: 0, skipped: 0, failed: 0 };
      const errors: string[] = [];
      for (let batch = 0; batch < maxBatches; batch += 1) {
        const summary = await runKnowledgeHubDocumentIndex(documentLimit, false, offset, false);
        lastSummary = summary;
        Object.keys(aggregate).forEach((key) => {
          aggregate[key as keyof typeof aggregate] += Number(summary.stats[key as keyof typeof summary.stats] || 0);
        });
        errors.push(...(summary.errorMessages || []));
        offset += summary.stats.scanned;
        setMessage({ type: 'info', text: `Dokumentindexering: ${offset} av cirka ${expectedTotal || '?'} källor scannade...` });
        if (summary.stats.scanned < documentLimit) {
          await runKnowledgeHubDocumentIndex(1, false, offset, true);
          break;
        }
      }
      if (!lastSummary) throw new Error('Dokumentindexeringen startade inte.');
      const combinedSummary: KnowledgeHubIndexSummary = {
        ...lastSummary,
        status: aggregate.failed > 0 ? 'failed' : 'completed',
        offset: 0,
        limit: offset,
        stats: aggregate,
        errorMessages: errors,
      };
      setDocumentSummary(combinedSummary);
      setDocumentCoverage(await getKnowledgeHubDocumentCoverage());
      setIndexCoverage(await getKnowledgeHubIndexCoverage());
      setMessage({
        type: combinedSummary.status === 'completed' ? 'success' : 'error',
        text: combinedSummary.status === 'completed' ? 'Full dokumentindexering klar.' : 'Dokumentindexering klar med fel. Se summeringen.',
      });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setDocumentIndexing(false);
    }
  };

  const handleRunForcedDocumentBatch = async () => {
    try {
      setDocumentForceIndexing(true);
      setMessage({ type: 'info', text: `Force-reindexerar ${documentLimit} dokumentkällor. OCR körs om backend-flaggan är på.` });
      const summary = await runKnowledgeHubDocumentIndex(documentLimit, true, 0, true, ['ocr-needed']);
      setDocumentSummary(summary);
      setDocumentCoverage(await getKnowledgeHubDocumentCoverage());
      setIndexCoverage(await getKnowledgeHubIndexCoverage());
      setMessage({
        type: summary.status === 'completed' ? 'success' : 'error',
        text: summary.status === 'completed' ? 'Force-reindex av dokumentbatch klar.' : 'Force-reindex klar med fel. Se summeringen.',
      });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setDocumentForceIndexing(false);
    }
  };

  const handleRunProfileIndex = async () => {
    try {
      setProfileIndexing(true);
      setMessage({ type: 'info', text: `Bygger profiler för upp till ${documentLimit} källor/assets...` });
      const summary = await runKnowledgeHubProfileIndex(documentLimit);
      setDocumentSummary(summary);
      setDocumentCoverage(await getKnowledgeHubDocumentCoverage());
      setIndexCoverage(await getKnowledgeHubIndexCoverage());
      setMessage({
        type: summary.status === 'completed' ? 'success' : 'error',
        text: summary.status === 'completed' ? 'Profilindexering klar.' : 'Profilindexering klar med fel. Se summeringen.',
      });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setProfileIndexing(false);
    }
  };

  const handleRunVectorIndex = async () => {
    try {
      setVectorIndexing(true);
      setMessage({ type: 'info', text: `Vector-indexerar upp till ${vectorLimit} chunks...` });
      const summary = await runKnowledgeHubVectorIndex(vectorLimit);
      setVectorSummary(summary);
      setIndexCoverage(await getKnowledgeHubIndexCoverage());
      const failedSuffix = summary.stats.failed > 0 ? ` (${summary.stats.failed} chunks misslyckades och kan köras om senare)` : '';
      setMessage({
        type: summary.status === 'completed' ? 'success' : 'error',
        text: summary.status === 'completed' ? `Vector-indexering klar.${failedSuffix}` : 'Vector-indexering klar med fel. Se summeringen.',
      });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setVectorIndexing(false);
    }
  };

  const handleRunFullVectorIndex = async () => {
    try {
      setFullVectorIndexing(true);
      let coverage = indexCoverage || await getKnowledgeHubIndexCoverage();
      let remaining = coverage.chunks.vectorRemaining;
      let guard = 0;
      let failedTotal = 0;
      let noProgressBatches = 0;
      const maxBatches = Math.max(25, Math.ceil(remaining / Math.max(1, vectorLimit)) + 25);

      setMessage({ type: 'info', text: `Startar vector-indexering för ${remaining} saknade chunks...` });
      while (remaining > 0 && guard < maxBatches && noProgressBatches < 3) {
        guard += 1;
        setMessage({ type: 'info', text: `Vector-indexerar batch ${guard}/${maxBatches}. Återstår cirka ${remaining} chunks...` });
        const summary = await runKnowledgeHubVectorIndex(vectorLimit);
        setVectorSummary(summary);
        coverage = await getKnowledgeHubIndexCoverage();
        setIndexCoverage(coverage);
        failedTotal += summary.stats.failed || 0;
        const progressCount = (summary.stats.created || 0) + (summary.stats.updated || 0);
        if (summary.status !== 'completed' && progressCount === 0) break;
        if (coverage.chunks.vectorRemaining >= remaining && progressCount === 0) {
          noProgressBatches += 1;
        } else {
          noProgressBatches = 0;
        }
        remaining = coverage.chunks.vectorRemaining;
      }

      setMessage({
        type: coverage.chunks.vectorRemaining === 0 ? 'success' : failedTotal > 0 ? 'info' : 'info',
        text: coverage.chunks.vectorRemaining === 0
          ? `Alla indexerade chunks har vector-indexerats.${failedTotal > 0 ? ` ${failedTotal} chunk-fel uppstod under körningen men stoppade inte indexeringen.` : ''}`
          : `Vector-indexering pausad. Återstår ${coverage.chunks.vectorRemaining} chunks.${failedTotal > 0 ? ` ${failedTotal} chunk-fel uppstod; kör igen för att försöka om dem.` : ''}`,
      });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setFullVectorIndexing(false);
    }
  };

  const handleSemanticSearch = async () => {
    if (!semanticQuery.trim()) return;
    try {
      setSemanticSearching(true);
      setSemanticResults(await searchKnowledgeHubSemantic(semanticQuery.trim(), 12));
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setSemanticSearching(false);
    }
  };

  const handleHybridSearch = async () => {
    if (!hybridQuery.trim()) return;
    try {
      setHybridSearching(true);
      setKnowledgeAnswer(null);
      setHybridResults(await searchKnowledgeHubHybrid(hybridQuery.trim(), 12));
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setHybridSearching(false);
    }
  };

  const handleKnowledgeAnswer = async (queryOverride?: string) => {
    const query = String(queryOverride || hybridQuery).trim();
    if (!query) return;
    try {
      setAnswering(true);
      setHybridQuery(query);
      setMessage({ type: 'info', text: 'Tar fram AI-svar från hybridträffarna...' });
      const historyForRequest = answerHistory.slice(-8);
      const response = await answerKnowledgeHubQuestion(query, 8, historyForRequest, activeAnswerContext, activeAnswerResultSet);
      setKnowledgeAnswer(response);
      setHybridResults(response.search);
      setActiveAnswerResultSet(buildAnswerActiveResultSet(response.search.results));
      const topEntity = response.search.results[0]?.entity;
      if (topEntity) {
        setActiveAnswerContext(buildAnswerActiveContext(topEntity));
      }
      const nextHistoryItems: KnowledgeHubAnswerHistoryItem[] = [
        { role: 'user', content: query, query },
        { role: 'assistant', content: response.answer, query },
      ];
      setAnswerHistory((current) => [
        ...current,
        ...nextHistoryItems,
      ].slice(-8));
      setMessage({ type: 'success', text: 'AI-svar klart.' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setAnswering(false);
    }
  };

  const handleArticleLookup = async () => {
    if (!articleNumber.trim()) return;
    try {
      setLookingUpArticle(true);
      setArticleResults(await lookupKnowledgeHubArticle(articleNumber.trim(), 12));
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setLookingUpArticle(false);
    }
  };

  const setHybridResultFeedback = (entityId: string, value: HybridFeedbackValue) => {
    setHybridFeedback((current) => {
      const next = { ...current };
      if (next[entityId] === value) {
        delete next[entityId];
      } else {
        next[entityId] = value;
      }
      return next;
    });
  };

  const handleRunAutoIndexOnce = async () => {
    try {
      setAutoIndexAction('run-once');
      await runKnowledgeHubAutoIndexOnce();
      await loadAutoIndexQueue();
      setMessage({ type: 'success', text: 'Autoindex-worker kördes en gång.' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setAutoIndexAction(null);
    }
  };

  const handleRetryAutoIndexJob = async (jobId: string) => {
    try {
      setAutoIndexAction(`retry:${jobId}`);
      await retryKnowledgeHubAutoIndexJob(jobId);
      await loadAutoIndexQueue();
      setMessage({ type: 'success', text: 'Köjobbet lades tillbaka i kön.' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setAutoIndexAction(null);
    }
  };

  const handleCancelAutoIndexJob = async (jobId: string) => {
    try {
      setAutoIndexAction(`cancel:${jobId}`);
      await cancelKnowledgeHubAutoIndexJob(jobId);
      await loadAutoIndexQueue();
      setMessage({ type: 'success', text: 'Köjobbet avbröts.' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setAutoIndexAction(null);
    }
  };

  const indexErrorMessages = indexSummary?.errorMessages || [];
  const indexedSamples = indexSummary?.sampleEntities || [];
  const documentErrorMessages = documentSummary?.errorMessages || [];
  const vectorErrorMessages = vectorSummary?.errorMessages || [];
  const assetCoveragePercent = indexCoverage?.assets?.coveragePercent || 0;
  const vectorCoveragePercent = indexCoverage?.chunks?.vectorCoveragePercent || 0;
  const answerSuggestedRefinements = knowledgeAnswer?.suggestedRefinements || [];
  const answerParagraphs = knowledgeAnswer ? getAnswerParagraphs(knowledgeAnswer.answer) : [];
  const answerHistoryTurns = Math.floor(answerHistory.length / 2);
  const answerHistoryText = formatAnswerHistory(answerHistory, activeAnswerContext, activeAnswerResultSet);
  const activeResultSetCount = activeAnswerResultSet?.items.length || 0;
  const answerUsesExpandedRetrieval = !!knowledgeAnswer
    && knowledgeAnswer.retrievalQuery
    && knowledgeAnswer.retrievalQuery !== knowledgeAnswer.query;
  const filteredHybridResults = (hybridResults?.results || []).filter((result) => {
    const entity = result.entity;
    if (hybridTypeFilter !== 'all' && entity.entityType !== hybridTypeFilter) return false;
    if (hybridOnlyImages && entity.entityType !== 'image') return false;
    if (hybridExcludeBanner && isBannerEntity(entity)) return false;
    if (hybridExcludeRecipe && isRecipeEntity(entity)) return false;
    return true;
  });

  const handleChatSideChange = (_event: React.MouseEvent<HTMLElement>, nextSide: KnowledgeHubChatSide | null) => {
    if (!nextSide) return;
    setChatSide(nextSide);
    writeChatSide(nextSide);
    setMessage({
      type: 'success',
      text: `Chatta med Athena öppnas nu från ${nextSide === 'left' ? 'vänster' : 'höger'} sida.`,
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200 }}>
      <Stack spacing={3}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <AutoAwesomeIcon sx={{ color: '#072f53' }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#072f53' }}>
              Chatta med Athena testpanel
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
            Intern testvy för de första Athena-stegen. Här kan du köra en liten indexering och söka i de
            nya `knowledge_*`-collections. Vector/Qdrant-delarna visas när motsvarande backend-flaggor är på.
          </Typography>
        </Box>

        {message && (
          <Alert severity={message.type} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Status
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Visar om modulen är aktiv och vilka delar som är påslagna.
                </Typography>
              </Box>
              <Button variant="outlined" onClick={loadStatus} disabled={loadingStatus}>
                {loadingStatus ? <CircularProgress size={18} /> : 'Uppdatera status'}
              </Button>
            </Stack>
            {status && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip color={status.enabled ? 'success' : 'default'} label={`Modul: ${status.enabled ? 'på' : 'av'}`} />
                <Chip color={status.vectorEnabled ? 'success' : 'default'} label={`Vector: ${status.vectorEnabled ? 'på' : 'av'}`} />
                <Chip color={status.vector?.embeddingsEnabled ? 'success' : 'default'} label={`Embeddings: ${status.vector?.embeddingsEnabled ? 'på' : 'av'}`} />
                <Chip color={status.capabilities.indexing ? 'info' : 'default'} label={`Indexering: ${status.capabilities.indexing ? 'tillgänglig' : 'av'}`} />
                {status.vector?.qdrantCollection && (
                  <Chip label={`Qdrant: ${status.vector.qdrantCollection}`} variant="outlined" />
                )}
                <Chip label={`Collections: ${status.collections.length}`} variant="outlined" />
              </Stack>
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: '#e5e7eb' }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Automatisk indexeringskö
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Visar om upload/Inhouse-triggers samlar jobb i Knowledge Hub-kön och om workern är aktiv.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button variant="outlined" onClick={loadAutoIndexQueue} disabled={loadingAutoIndex}>
                  {loadingAutoIndex ? <CircularProgress size={18} /> : 'Uppdatera kö'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRunAutoIndexOnce}
                  disabled={autoIndexAction !== null || !autoIndexStatus?.workerEnabled}
                >
                  {autoIndexAction === 'run-once' ? <CircularProgress size={18} color="inherit" /> : 'Kör worker en gång'}
                </Button>
              </Stack>
            </Stack>

            {autoIndexStatus ? (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip color={autoIndexStatus.enabled ? 'success' : 'default'} label={`Autoindex: ${autoIndexStatus.enabled ? 'på' : 'av'}`} />
                  <Chip color={autoIndexStatus.workerEnabled ? 'success' : 'default'} label={`Worker: ${autoIndexStatus.workerEnabled ? 'på' : 'av'}`} />
                  <Chip variant="outlined" label={`Debounce: ${Math.round(autoIndexStatus.debounceMs / 1000)}s`} />
                  <Chip variant="outlined" label={`Max försök: ${autoIndexStatus.maxAttempts}`} />
                  <Chip color={(autoIndexStatus.statusCounts.failed || 0) > 0 ? 'error' : 'default'} variant="outlined" label={`Misslyckade: ${autoIndexStatus.statusCounts.failed || 0}`} />
                  <Chip color={(autoIndexStatus.statusCounts.queued || 0) > 0 ? 'info' : 'default'} variant="outlined" label={`Köade: ${autoIndexStatus.statusCounts.queued || 0}`} />
                  <Chip color={(autoIndexStatus.statusCounts.running || 0) > 0 ? 'info' : 'default'} variant="outlined" label={`Körs: ${autoIndexStatus.statusCounts.running || 0}`} />
                </Stack>
                {!autoIndexStatus.enabled && (
                  <Alert severity="info">
                    Automatisk indexering är avstängd. Upload och Inhouse fungerar som vanligt, men inga nya Knowledge Hub-jobb skapas.
                  </Alert>
                )}
                {autoIndexStatus.enabled && !autoIndexStatus.workerEnabled && (
                  <Alert severity="warning">
                    Queue-only är aktivt: triggers skapar köjobb, men workern kör inte automatiskt förrän `KNOWLEDGE_HUB_AUTOINDEX_WORKER_ENABLED=true`.
                  </Alert>
                )}
                {autoIndexJobs.length > 0 ? (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Senaste köjobb
                    </Typography>
                    {autoIndexJobs.map((job) => (
                      <Paper key={job.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between">
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
                              <Chip
                                size="small"
                                color={job.status === 'failed' ? 'error' : job.status === 'completed' ? 'success' : 'default'}
                                label={job.status}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                {job.sourceType}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                {job.sourceId}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Orsak: {job.reason} · försök {job.attempts}/{job.maxAttempts}
                              {job.updatedAt ? ` · uppdaterad ${new Date(job.updatedAt).toLocaleString('sv-SE')}` : ''}
                            </Typography>
                            {job.error && (
                              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>
                                {job.error}
                              </Typography>
                            )}
                          </Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={autoIndexAction !== null || !['failed', 'partial', 'cancelled', 'skipped'].includes(job.status)}
                              onClick={() => handleRetryAutoIndexJob(job.id)}
                            >
                              {autoIndexAction === `retry:${job.id}` ? <CircularProgress size={16} /> : 'Retry'}
                            </Button>
                            <Button
                              size="small"
                              color="warning"
                              variant="outlined"
                              disabled={autoIndexAction !== null || !['queued', 'running'].includes(job.status)}
                              onClick={() => handleCancelAutoIndexJob(job.id)}
                            >
                              {autoIndexAction === `cancel:${job.id}` ? <CircularProgress size={16} /> : 'Avbryt'}
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Inga köjobb visas ännu.
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Köstatus är inte hämtad ännu.
              </Typography>
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#fbfdff', borderColor: '#dbeafe' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#072f53' }}>
                Chattfönster
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Välj vilken sida Chatta med Athena ska öppnas från. Inställningen sparas i webbläsaren.
              </Typography>
            </Box>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={chatSide}
              onChange={handleChatSideChange}
              aria-label="Välj sida för Chatta med Athena"
              sx={{
                bgcolor: '#ffffff',
                '& .MuiToggleButton-root': {
                  px: 2,
                  fontWeight: 800,
                  textTransform: 'none',
                },
              }}
            >
              <ToggleButton value="left" aria-label="Öppna chatten från vänster">
                Vänster
              </ToggleButton>
              <ToggleButton value="right" aria-label="Öppna chatten från höger">
                Höger
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                1. Indexera testdata
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Batcha kontrollerat. “Alla”-frågor gäller bara det som syns som indexerat här.
              </Typography>
            </Box>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fafafa' }}>
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                  <Typography variant="subtitle2">
                    Index-täckning
                  </Typography>
                  <Button size="small" variant="text" onClick={loadIndexCoverage} disabled={loadingCoverage}>
                    {loadingCoverage ? <CircularProgress size={16} /> : 'Uppdatera täckning'}
                  </Button>
                </Stack>
                {indexCoverage?.assets && indexCoverage?.chunks ? (
                  <>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="body2">
                          Assets: {indexCoverage.assets.indexed} av {indexCoverage.assets.total}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {assetCoveragePercent.toFixed(1)}%
                        </Typography>
                      </Stack>
                      <LinearProgress variant="determinate" value={Math.min(100, assetCoveragePercent)} sx={{ mt: 0.5 }} />
                    </Box>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="body2">
                          Vectors: {indexCoverage.chunks.vectorIndexed} av {indexCoverage.chunks.total} chunks
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {vectorCoveragePercent.toFixed(1)}%
                        </Typography>
                      </Stack>
                      <LinearProgress variant="determinate" color="secondary" value={Math.min(100, vectorCoveragePercent)} sx={{ mt: 0.5 }} />
                    </Box>
                    {documentCoverage && (
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <StatChip label="Dokument" value={documentCoverage.documents} color="info" />
                          <StatChip label="Dokumentchunks" value={documentCoverage.documentChunks} />
                          <StatChip label="OCR chunks" value={documentCoverage.ocrChunks || 0} color={(documentCoverage.ocrChunks || 0) > 0 ? 'success' : 'default'} />
                          <StatChip label="Valuable chunks" value={documentCoverage.valuableChunks || 0} color={(documentCoverage.valuableChunks || 0) > 0 ? 'success' : 'default'} />
                          <StatChip label="Textklassade chunks" value={documentCoverage.typedDocumentChunks || 0} />
                          <StatChip label="Profiler" value={documentCoverage.profiles} color="success" />
                        </Stack>
                        {documentCoverage.diagnostics && (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap">
                            <Chip size="small" variant="outlined" label={`OCR ${documentCoverage.diagnostics.ocrEnabled ? 'på' : 'av'}`} />
                            <Chip size="small" variant="outlined" label={`Page-aware ${documentCoverage.diagnostics.pageAwareDocuments ? 'på' : 'av'}`} />
                            <Chip size="small" color={(documentCoverage.diagnostics.needsOcr || 0) > 0 ? 'warning' : 'default'} variant="outlined" label={`Behöver OCR: ${documentCoverage.diagnostics.needsOcr || 0}`} />
                            <Chip size="small" color={(documentCoverage.diagnostics.lowQuality || 0) > 0 ? 'warning' : 'default'} variant="outlined" label={`Låg kvalitet: ${documentCoverage.diagnostics.lowQuality || 0}`} />
                            <Chip size="small" variant="outlined" label={`Sidmappade: ${documentCoverage.diagnostics.pageMapped || 0}`} />
                          </Stack>
                        )}
                        <Stack direction="row" spacing={0.75} flexWrap="wrap">
                          {Object.entries(documentCoverage.bySourceKind || {}).map(([kind, coverage]) => (
                            <Chip
                              key={kind}
                              size="small"
                              variant="outlined"
                              color={coverage.failed || coverage.stale || coverage.needsOcr ? 'warning' : 'default'}
                              label={`${kind}: ${coverage.indexed}/${coverage.total} · kvar ${coverage.remaining} · tom/fel ${coverage.failed} · OCR ${coverage.needsOcr} · låg ${coverage.lowQuality || 0} · sidor ${coverage.pageMapped || 0} · stale ${coverage.stale}`}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    )}
                    {indexCoverage.assets.remaining > 0 && (
                      <Alert severity="info">
                        “Alla”-frågor är bara kompletta för indexerad data. Återstår {indexCoverage.assets.remaining} assets.
                      </Alert>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Klicka “Uppdatera täckning” för att se hur mycket som är indexerat.
                  </Typography>
                )}
              </Stack>
            </Paper>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField
                label="Batchstorlek assets"
                type="number"
                size="small"
                value={indexLimit}
                inputProps={{ min: 1, max: 1000 }}
                onChange={(event) => setIndexLimit(Math.max(1, Math.min(1000, Number(event.target.value || 1))))}
                sx={{ width: 160 }}
              />
              <Button
                variant="contained"
                startIcon={indexing ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                onClick={handleRunIndex}
                disabled={indexing || fullIndexing}
              >
                Indexera första batch
              </Button>
              <Button
                variant="outlined"
                startIcon={indexing ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                onClick={handleRunNextIndexBatch}
                disabled={indexing || fullIndexing}
              >
                Nästa batch
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={fullIndexing ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                onClick={handleRunFullAssetIndex}
                disabled={indexing || fullIndexing}
              >
                Indexera alla assets
              </Button>
            </Stack>
            <Divider />
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Dokument och profiler
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Indexerar Inhouse/Listningar PDF:er, DAM-dokument och Brand Guide som ett separat Knowledge Hub-lager.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <TextField
                  label="Batchstorlek dokument"
                  type="number"
                  size="small"
                  value={documentLimit}
                  inputProps={{ min: 1, max: 500 }}
                  onChange={(event) => setDocumentLimit(Math.max(1, Math.min(500, Number(event.target.value || 1))))}
                  sx={{ width: 180 }}
                />
                <Button
                  variant="outlined"
                  startIcon={documentIndexing ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                  onClick={handleRunDocumentIndex}
                  disabled={documentIndexing || documentForceIndexing || profileIndexing}
                >
                  Indexera dokument
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={documentForceIndexing ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                  onClick={handleRunForcedDocumentBatch}
                  disabled={documentIndexing || documentForceIndexing || profileIndexing}
                >
                  Force reindex/OCR batch
                </Button>
                <Button
                  variant="outlined"
                  startIcon={profileIndexing ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}
                  onClick={handleRunProfileIndex}
                  disabled={documentIndexing || documentForceIndexing || profileIndexing}
                >
                  Bygg profiler
                </Button>
              </Stack>
              {documentSummary && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip size="small" label={`Status: ${documentSummary.status}`} color={documentSummary.status === 'completed' ? 'success' : 'error'} />
                  <StatChip label="Scannade" value={documentSummary.stats.scanned} />
                  <StatChip label="Skapade" value={documentSummary.stats.created} color="success" />
                  <StatChip label="Uppdaterade" value={documentSummary.stats.updated} color="info" />
                  <StatChip label="Skippade" value={documentSummary.stats.skipped} />
                  <StatChip label="Fel" value={documentSummary.stats.failed} color={documentSummary.stats.failed ? 'error' : 'default'} />
                </Stack>
              )}
              {documentErrorMessages.length > 0 && (
                <Alert severity="warning">
                  {documentErrorMessages.slice(0, 5).join('\n')}
                </Alert>
              )}
            </Stack>
            <Divider />
            <DocumentTextInspector />
            {indexSummary && (
              <Box sx={{ pt: 1 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip color={indexSummary.status === 'completed' ? 'success' : 'error'} label={indexSummary.status} />
                  <StatChip label="Scanned" value={indexSummary.stats.scanned} color="info" />
                  <StatChip label="Created" value={indexSummary.stats.created} color="success" />
                  <StatChip label="Updated" value={indexSummary.stats.updated} color="info" />
                  <StatChip label="Skipped" value={indexSummary.stats.skipped} />
                  <StatChip label="Failed" value={indexSummary.stats.failed} color={indexSummary.stats.failed ? 'error' : 'default'} />
                </Stack>
                {indexErrorMessages.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {indexErrorMessages.slice(0, 3).join('\n')}
                  </Alert>
                )}
                {indexedSamples.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Senast indexerade assets
                    </Typography>
                    <Stack spacing={1}>
                      {indexedSamples.map((entity) => {
                        const articleNumbers = entity.articleNumbers || [];

                        return (
                        <Card key={entity.id} variant="outlined" sx={{ borderRadius: 2, bgcolor: '#fafafa' }}>
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              spacing={1.5}
                              alignItems={{ md: 'center' }}
                              justifyContent="space-between"
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <Chip size="small" color="primary" label={entity.entityType} />
                                  {articleNumbers.slice(0, 3).map((articleNumber) => (
                                    <Chip key={articleNumber} size="small" variant="outlined" label={articleNumber} />
                                  ))}
                                </Stack>
                                <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.75 }}>
                                  {entity.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {entity.fileName || entity.assetId || entity.id}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setSearchQuery(entity.title);
                                    void searchKnowledgeHubEntities(entity.title, 12).then(setSearchResults);
                                  }}
                                >
                                  Sök titel
                                </Button>
                                {articleNumbers[0] && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      setArticleNumber(articleNumbers[0]);
                                      void lookupKnowledgeHubArticle(articleNumbers[0], 12).then(setArticleResults);
                                    }}
                                  >
                                    Sök artikel
                                  </Button>
                                )}
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </Paper>

        <Accordion disableGutters sx={{ borderRadius: 3, border: '1px solid #d9e7f7', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                2. Sök i indexet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Äldre testverktyg för direkt sökning i indexet.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Sökfras"
                size="small"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void handleSearch();
                }}
                fullWidth
              />
              <Button
                variant="contained"
                startIcon={searching ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                onClick={handleSearch}
                disabled={searching}
                sx={{ minWidth: 140 }}
              >
                Sök
              </Button>
            </Stack>
            {searchResults && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">
                  {searchResults.count} träffar för “{searchResults.query}”
                </Typography>
                {searchResults.results.map((entity) => (
                  <EntityCard key={entity._id} entity={entity} />
                ))}
              </Stack>
            )}
          </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters sx={{ borderRadius: 3, border: '1px solid #d9e7f7', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                3. Artikelnummerlookup
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Äldre testverktyg för artikelnummer och metadatafält.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Artikelnummer"
                size="small"
                value={articleNumber}
                onChange={(event) => setArticleNumber(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void handleArticleLookup();
                }}
                fullWidth
              />
              <Button
                variant="outlined"
                startIcon={lookingUpArticle ? <CircularProgress size={18} /> : <SearchIcon />}
                onClick={handleArticleLookup}
                disabled={lookingUpArticle}
                sx={{ minWidth: 170 }}
              >
                Sök artikel
              </Button>
            </Stack>
            {articleResults && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">
                  {articleResults.count} träffar för artikel “{articleResults.articleNumber}”
                </Typography>
                {articleResults.results.map((entity) => (
                  <EntityCard key={entity._id} entity={entity} />
                ))}
              </Stack>
            )}
          </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters sx={{ borderRadius: 3, border: '1px solid #d9e7f7', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                4. Semantisk sökning
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Äldre testverktyg för Qdrant, embeddings och vector-indexering.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={2}>

            {!status?.vectorEnabled && (
              <Alert severity="info">
                Vector search är avstängt. Sätt `KNOWLEDGE_HUB_VECTOR_ENABLED=true` och `KNOWLEDGE_HUB_EMBEDDINGS_ENABLED=true`
                i backend-miljön när Qdrant körs.
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField
                label="Antal chunks"
                type="number"
                size="small"
                value={vectorLimit}
                inputProps={{ min: 1, max: 1000 }}
                onChange={(event) => setVectorLimit(Math.max(1, Math.min(1000, Number(event.target.value || 1))))}
                sx={{ width: 160 }}
              />
              <Button
                variant="outlined"
                startIcon={vectorIndexing ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                onClick={handleRunVectorIndex}
                disabled={vectorIndexing || fullVectorIndexing}
              >
                Indexera vector-batch
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={fullVectorIndexing ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                onClick={handleRunFullVectorIndex}
                disabled={vectorIndexing || fullVectorIndexing || !status?.vectorEnabled}
              >
                Indexera alla saknade vectors
              </Button>
            </Stack>

            {vectorSummary && (
              <Box sx={{ pt: 1 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip color={vectorSummary.status === 'completed' ? 'success' : 'error'} label={vectorSummary.status} />
                  <StatChip label="Scanned" value={vectorSummary.stats.scanned} color="info" />
                  <StatChip label="Created" value={vectorSummary.stats.created} color="success" />
                  <StatChip label="Updated" value={vectorSummary.stats.updated} color="info" />
                  <StatChip label="Skipped" value={vectorSummary.stats.skipped} />
                  <StatChip label="Failed" value={vectorSummary.stats.failed} color={vectorSummary.stats.failed ? 'error' : 'default'} />
                  <Chip size="small" variant="outlined" label={vectorSummary.embeddingModel} />
                </Stack>
                {vectorErrorMessages.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {vectorErrorMessages.slice(0, 3).join('\n')}
                  </Alert>
                )}
              </Box>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Semantisk fråga"
                size="small"
                value={semanticQuery}
                onChange={(event) => setSemanticQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void handleSemanticSearch();
                }}
                fullWidth
              />
              <Button
                variant="contained"
                startIcon={semanticSearching ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                onClick={handleSemanticSearch}
                disabled={semanticSearching}
                sx={{ minWidth: 170 }}
              >
                Sök semantiskt
              </Button>
            </Stack>

            {semanticResults && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">
                  {semanticResults.count} semantiska träffar för “{semanticResults.query}”
                </Typography>
                {semanticResults.results.map((result) => (
                  <Card key={result.chunk.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip size="small" color="primary" label={`Score: ${result.score.toFixed(3)}`} />
                          <Chip size="small" variant="outlined" label={semanticResults.embeddingModel} />
                        </Stack>
                        <EntityCard entity={result.entity} />
                        <Typography variant="body2" color="text.secondary">
                          {result.chunk.textSnippet}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters sx={{ borderRadius: 3, border: '1px solid #d9e7f7', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                5. Hybrid-sökning
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Äldre testverktyg för hybridresultat och legacy AI-svar.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={2}>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Hybridfråga"
                size="small"
                value={hybridQuery}
                onChange={(event) => setHybridQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void handleHybridSearch();
                }}
                fullWidth
              />
              <Button
                variant="contained"
                startIcon={hybridSearching ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                onClick={handleHybridSearch}
                disabled={hybridSearching}
                sx={{ minWidth: 170 }}
              >
                Sök hybrid
              </Button>
              <Button
                variant="outlined"
                startIcon={answering ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}
                onClick={() => void handleKnowledgeAnswer()}
                disabled={answering}
                sx={{ minWidth: 170 }}
              >
                Svara med AI
              </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fafafa' }}>
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Lokal testpanel-historik: {answerHistoryTurns} samtalsvändor. Används för följdfrågor som “denna produkt”.
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Aktiv context: {activeAnswerContext ? activeAnswerContext.title : 'ingen'}
                    </Typography>
                    {activeAnswerContext?.fileName && (
                      <Typography variant="caption" color="text.secondary">
                        {activeAnswerContext.fileName}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>
                      Aktiv lista: {activeResultSetCount > 0 ? `${activeResultSetCount} träffar` : 'ingen'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="text"
                      disabled={answerHistory.length === 0}
                      onClick={() => {
                        void navigator.clipboard.writeText(answerHistoryText).then(() => {
                          setMessage({ type: 'success', text: 'Chatthistoriken kopierades.' });
                        }).catch((error) => {
                          setMessage({ type: 'error', text: getErrorMessage(error) });
                        });
                      }}
                    >
                      Kopiera historik
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      disabled={!activeAnswerContext}
                      onClick={() => setActiveAnswerContext(null)}
                    >
                      Rensa context
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      disabled={!activeAnswerResultSet}
                      onClick={() => setActiveAnswerResultSet(null)}
                    >
                      Rensa lista
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      disabled={answerHistory.length === 0 && !activeAnswerContext && !activeAnswerResultSet}
                      onClick={() => {
                        setAnswerHistory([]);
                        setActiveAnswerContext(null);
                        setActiveAnswerResultSet(null);
                        setKnowledgeAnswer(null);
                      }}
                    >
                      Rensa chatthistorik
                    </Button>
                  </Stack>
                </Stack>
                {answerHistory.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: '#fff', maxHeight: 220, overflow: 'auto' }}>
                    <Stack spacing={0.75}>
                      {answerHistory.map((item, index) => (
                        <Box key={`${index}-${item.role}`}>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            {item.role === 'user' ? 'Du' : 'AI'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.content}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Paper>

            {knowledgeAnswer && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f7fbff', borderColor: '#bbdefb' }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip size="small" color="info" label="AI-svar" />
                    <Chip size="small" variant="outlined" label={knowledgeAnswer.model} />
                    <Chip
                      size="small"
                      color={getConfidenceColor(knowledgeAnswer.confidence)}
                      variant={getConfidenceLevel(knowledgeAnswer.confidence) === 'medium' ? 'outlined' : 'filled'}
                      label={getConfidenceLabel(knowledgeAnswer.confidence)}
                    />
                    <Chip size="small" variant="outlined" label={`${knowledgeAnswer.sources.length} källor`} />
                    {answerUsesExpandedRetrieval && (
                      <Chip size="small" color="warning" variant="outlined" label="Följdfråga tolkad med historik" />
                    )}
                  </Stack>
                  {answerUsesExpandedRetrieval && (
                    <Typography variant="caption" color="text.secondary">
                      Hämtade källor med: {knowledgeAnswer.retrievalQuery}
                    </Typography>
                  )}
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#ffffff' }}>
                    <Stack spacing={1}>
                      {answerParagraphs.map((paragraph, index) => (
                        <Typography key={`${index}-${paragraph}`} variant="body1">
                          {paragraph}
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                  {knowledgeAnswer.clarifyingQuestion && (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      {knowledgeAnswer.clarifyingQuestion}
                    </Alert>
                  )}
                  {answerSuggestedRefinements.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Testa en mer precis fråga:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
                        {answerSuggestedRefinements.map((refinement) => (
                          <Button
                            key={refinement}
                            size="small"
                            variant="outlined"
                            onClick={() => void handleKnowledgeAnswer(refinement)}
                            disabled={answering}
                          >
                            {refinement}
                          </Button>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {knowledgeAnswer.search.results.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Källor i svaret
                      </Typography>
                      <Stack spacing={1.25}>
                        {knowledgeAnswer.search.results.slice(0, 8).map((result, index) => {
                          const source = knowledgeAnswer.sources.find((item) => item.entityId === result.entity._id);
                          const referenceNumber = source?.referenceNumber || index + 1;

                          return (
                            <Paper key={result.entity._id} variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fff' }}>
                              <Stack spacing={1}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <Chip size="small" color="primary" label={`Källa [${referenceNumber}]`} />
                                  <Chip size="small" variant="outlined" label={`Score: ${result.score.toFixed(2)}`} />
                                  {typeof result.semanticScore === 'number' && (
                                    <Chip size="small" variant="outlined" label={`Semantic: ${result.semanticScore.toFixed(2)}`} />
                                  )}
                                  <Button
                                    size="small"
                                    variant={activeAnswerContext?.entityId === result.entity._id ? 'contained' : 'outlined'}
                                    onClick={() => setActiveAnswerContext(buildAnswerActiveContext(result.entity))}
                                  >
                                    Använd som context
                                  </Button>
                                </Stack>
                                <EntityCard entity={result.entity} />
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

            {hybridResults && (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fafafa' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} flexWrap="wrap">
                  <TextField
                    select
                    label="Resultattyp"
                    size="small"
                    value={hybridTypeFilter}
                    onChange={(event) => setHybridTypeFilter(event.target.value as HybridEntityTypeFilter)}
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="all">Alla typer</MenuItem>
                    <MenuItem value="image">Bilder</MenuItem>
                    <MenuItem value="recipe">Recept</MenuItem>
                    <MenuItem value="wine">Vin</MenuItem>
                    <MenuItem value="product">Produkter</MenuItem>
                    <MenuItem value="document">Dokument</MenuItem>
                    <MenuItem value="salesSheet">Säljblad</MenuItem>
                    <MenuItem value="asset">Övriga assets</MenuItem>
                  </TextField>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={hybridOnlyImages}
                        onChange={(event) => setHybridOnlyImages(event.target.checked)}
                      />
                    )}
                    label="Endast bilder"
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={hybridExcludeBanner}
                        onChange={(event) => setHybridExcludeBanner(event.target.checked)}
                      />
                    )}
                    label="Exkludera banner"
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={hybridExcludeRecipe}
                        onChange={(event) => setHybridExcludeRecipe(event.target.checked)}
                      />
                    )}
                    label="Exkludera recept"
                  />
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      setHybridTypeFilter('all');
                      setHybridOnlyImages(false);
                      setHybridExcludeBanner(false);
                      setHybridExcludeRecipe(false);
                    }}
                  >
                    Rensa filter
                  </Button>
                </Stack>
              </Paper>
            )}

            {hybridResults && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">
                  {filteredHybridResults.length} av {hybridResults.count} hybridträffar för “{hybridResults.query}”
                </Typography>
                {hybridResults.warnings.length > 0 && (
                  <Alert severity="warning">
                    Semantiska delen kunde inte köras: {hybridResults.warnings.slice(0, 2).join(' | ')}
                  </Alert>
                )}
                {filteredHybridResults.length === 0 && (
                  <Alert severity="info">
                    Inga träffar matchar de valda filtren. Rensa filter eller välj en annan resultattyp.
                  </Alert>
                )}
                {filteredHybridResults.map((result) => {
                  const feedback = hybridFeedback[result.entity._id];

                  return (
                    <Card
                      key={result.entity._id}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        borderColor: feedback === 'good' ? 'success.main' : feedback === 'bad' ? 'error.main' : undefined,
                        bgcolor: feedback === 'good' ? '#f1f8e9' : feedback === 'bad' ? '#ffebee' : undefined,
                      }}
                    >
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip size="small" color="primary" label={`Hybrid: ${result.score.toFixed(3)}`} />
                            {typeof result.textScore === 'number' && (
                              <Chip size="small" variant="outlined" label={`Text: ${result.textScore.toFixed(3)}`} />
                            )}
                            {typeof result.semanticScore === 'number' && (
                              <Chip size="small" variant="outlined" label={`Semantic: ${result.semanticScore.toFixed(3)}`} />
                            )}
                            {result.sources.map((source) => (
                              <Chip key={source} size="small" color={source === 'semantic' ? 'info' : 'success'} label={source} />
                            ))}
                          </Stack>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {result.matchReasons.map((reason) => (
                              <Chip key={reason} size="small" variant="outlined" label={reason} />
                            ))}
                          </Stack>
                          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                            <Button
                              size="small"
                              variant={feedback === 'good' ? 'contained' : 'outlined'}
                              color="success"
                              startIcon={<ThumbUpIcon />}
                              onClick={() => setHybridResultFeedback(result.entity._id, 'good')}
                            >
                              Bra träff
                            </Button>
                            <Button
                              size="small"
                              variant={feedback === 'bad' ? 'contained' : 'outlined'}
                              color="error"
                              startIcon={<ThumbDownIcon />}
                              onClick={() => setHybridResultFeedback(result.entity._id, 'bad')}
                            >
                              Dålig träff
                            </Button>
                            {feedback && (
                              <Typography variant="caption" color={feedback === 'good' ? 'success.main' : 'error.main'}>
                                Markerad som {feedback === 'good' ? 'bra' : 'dålig'} träff lokalt i testpanelen.
                              </Typography>
                            )}
                          </Stack>
                          <EntityCard entity={result.entity} />
                          {result.chunk?.textSnippet && (
                            <Typography variant="body2" color="text.secondary">
                              {result.chunk.textSnippet}
                            </Typography>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Stack>
          </AccordionDetails>
        </Accordion>

        <Divider />
        <Alert severity="info">
          Nästa riktiga produkt-UI blir drawer/chatten. Den här panelen är bara för att säkert testa indexering och sökning.
        </Alert>
      </Stack>
    </Box>
  );
};

const KnowledgeHubTestPage: React.FC = () => {
  const canAdmin = useKnowledgeHubAdminAccess();
  if (!canAdmin) {
    return (
      <Alert severity="warning">
        Du saknar behörigheten knowledgeHub:admin för Athenas inspektions- och driftverktyg.
      </Alert>
    );
  }
  return <KnowledgeHubTestPageContent />;
};

export default KnowledgeHubTestPage;
